import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, Info } from 'lucide-react';

interface ConfirmDialogProps {
    isOpen: boolean;
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
    variant?: 'danger' | 'default';
    /** Contenuto extra (es. input di conferma) mostrato tra descrizione e bottoni */
    children?: React.ReactNode;
    /** Disabilita il bottone di conferma (es. finché non si digita la parola richiesta) */
    confirmDisabled?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    isOpen,
    title,
    description,
    confirmLabel,
    cancelLabel,
    onCancel,
    onConfirm,
    loading = false,
    variant = 'danger',
    children,
    confirmDisabled = false,
}) => {
    const { t } = useTranslation();
    const defaultCancelLabel = t('common.cancel');

    if (!isOpen) return null;

    const effectiveCancelLabel = cancelLabel || defaultCancelLabel;
    const isDanger = variant === 'danger';

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">

            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                onClick={loading ? undefined : onCancel}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200 border border-gray-100">

                {/* Icona in alto */}
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isDanger ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-900'
                    }`}>
                    {isDanger ? <AlertTriangle size={24} /> : <Info size={24} />}
                </div>

                <h2 className="text-lg font-bold text-gray-900 text-center leading-tight">
                    {title}
                </h2>

                <p className="mt-2 text-sm text-gray-500 text-center leading-relaxed">
                    {description}
                </p>

                {children && <div className="mt-4">{children}</div>}

                {/* Bottoni affiancati (migliore UX per dialog) */}
                <div className="mt-6 flex gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="flex-1 py-2.5 rounded-xl border border-gray-200 font-bold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm"
                    >
                        {effectiveCancelLabel}
                    </button>

                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading || confirmDisabled}
                        className={`flex-1 py-2.5 rounded-xl font-bold text-white shadow-lg disabled:opacity-70 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2 ${isDanger
                            ? 'bg-red-500 hover:bg-red-600 shadow-red-100'
                            : 'bg-gray-900 hover:bg-black shadow-gray-200'
                            }`}
                    >
                        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmDialog;